import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Card from 'reactstrap/lib/Card';
import Button from 'reactstrap/lib/Button';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import {
  getAppraisalLink,
  getAppraisalLinkTextFromVehicle,
} from 'site-modules/shared/utils/appraisal-link-constructor';
import { CORE_GALLERY_SIZE } from 'site-modules/core-page/utils/constants';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { BuildPriceButtonAd } from 'site-modules/shared/components/native-ad/build-price-button-ad/build-price-button-ad';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { InternationalZipPicker } from 'site-modules/shared/components/international-zip-picker/international-zip-picker';
import { EditorsChoiceBadge } from 'site-modules/shared/components/editors-choice-badge/editors-choice-badge';
import { PhotoGallery } from 'site-modules/core-page/components/photo-gallery/photo-gallery';
import { CoreHighlights } from 'site-modules/core-page/components/core-highlights/core-highlights';
import { CorePricingHistogram } from 'site-modules/core-page/components/core-pricing-histogram/core-pricing-histogram';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import { ListPriceRange } from './list-price-range';
import './used-core-intro.scss';

export function UsedCoreIntro({ params, vehicle, editorialReview, isInternational, isMobile }) {
  const { isReviewSubpage } = params;
  const {
    year,
    make: { slug: makeSlug },
    model: { slug: modelSlug },
    submodels: { slug: submodelSlug },
  } = vehicle;

  const displayVehicle = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    trimDuplicateSubmodel: true,
    updateChevroletMakeName: true,
    disableTrademarkCharacter: true,
    usedPrefix: isReviewSubpage ? '' : 'Used',
  });
  const title = isReviewSubpage ? `${displayVehicle} Review` : displayVehicle;

  const pricingHistogramFragment = (
    <Fragment>
      <CorePricingHistogram className="mb-1_5" />
      <Button
        tag={Link}
        to={getAppraisalLink({ make: makeSlug, model: modelSlug, year })}
        data-tracking-id="tool_select_appraise_your_car"
        size="lg"
        color="outline-blue-50"
        className="cta-button rounded-8 w-100 py-0_75 text-transform-none bg-white"
      >
        {isReviewSubpage && !isMobile ? getAppraisalLinkTextFromVehicle(vehicle) : 'Appraise This Car'}
      </Button>
    </Fragment>
  );

  return (
    <Container className="used-core-intro">
      <h1 className="intro-title heading-2 text-white mb-1">{title}</h1>
      <ListPriceRange
        className="text-white mb-1"
        valueClassName="d-inline-block fw-bold size-20"
        params={params}
        submodelSlug={submodelSlug}
        isUnknownZip={isInternational}
      />

      <ScrollElement className="intro-content pb-3_5" id="subnav-overview">
        <div className="intro-gallery pos-r mb-1 mb-md-0">
          <PhotoGallery
            params={params}
            isMobile={isMobile}
            vehicle={vehicle}
            editorialReview={editorialReview}
            photoSize={isMobile ? CORE_GALLERY_SIZE.MOBILE.HERO : CORE_GALLERY_SIZE.DESKTOP.HERO}
            isUsed
          />
          {isMobile && <EditorsChoiceBadge vehicleParams={params} className="m-0_5" />}
        </div>

        <div className="intro-highlights">
          <CoreHighlights
            params={params}
            vehicle={vehicle}
            editorialReview={editorialReview}
            isMobile={isMobile}
            isUsed
          />
        </div>

        <div className="intro-trims">
          {!isMobile && (
            <Card
              className="intro-trims-content rounded-12 bg-cool-gray-90 mt-1_5 mt-lg-0 px-1 py-1 px-lg-0_5 px-xl-1 m-lg-0"
              data-tracking-parent="edm-entry-model-summary"
            >
              {isInternational ? (
                <Experiment name="core-6829-used-nozip" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <InternationalZipPicker
                      btnTitle="Go"
                      isFullWidth
                      className="border-0"
                      btnClassName="btn-blue-50 size-16"
                      colConfig={{ zip: { xs: 4 }, btn: { xs: 4 } }}
                    />
                  </Recipe>
                  <Recipe name="chal">{pricingHistogramFragment}</Recipe>
                </Experiment>
              ) : (
                pricingHistogramFragment
              )}
              <BuildPriceButtonAd position={'1'} wrapperClass="mt-1_5 pt-1_5" linkUrlClass="text-blue-40" />
            </Card>
          )}
          {isMobile && <IncentivesAd wrapperClass="px-0 mt-2" mobile />}
        </div>
      </ScrollElement>
    </Container>
  );
}

UsedCoreIntro.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  editorialReview: EditorialReviewEntities.EditorialReview,
  isInternational: PropTypes.bool,
  isMobile: PropTypes.bool,
};

UsedCoreIntro.defaultProps = {
  editorialReview: null,
  isInternational: false,
  isMobile: false,
};
